import axios, { AxiosError } from "axios";
import { v4 as uuidv4 } from "uuid";
import { ConsentAction, getStorageMechanism, ResponseError, } from "@finarkein/aasdk-core";
import { getMappedLinkedAccounts, getConsentRequestDetailsMapped, getMappedDiscoveredAccounts, getDeviceType, getMappedLinkingRes, checkForAltMobileNumber, getFITypes, handleErrors, } from "./operations";
import { NadlCrypto } from "./services/cryto.service";
import { ResponseMapperNadl } from "./utils/ResponseMapperNadl";
export class NADLService {
    constructor() {
        this.availableStorage = getStorageMechanism();
        this.accountCategories = [];
        this.rejectionReasons = [];
        this.listOfFips = [];
        this.crypto = new NadlCrypto();
        this.sendRawResponse = false;
        this.xApiKey = "";
        this.timeout = 45000;
        this.noOfRetries = 5;
        this.delayTimeout = 3000;
        this.identifier = () => "nadl";
    }
    setup(initOptions) {
        this.baseUrl = initOptions.url;
        this.crypto.init(initOptions.cipher, initOptions.iv);
        const apiKey = initOptions.x_api_key;
        this.xApiKey = initOptions.x_api_key;
        if (initOptions.rawResponse) {
            this.sendRawResponse = true;
        }
        if (initOptions.timeout) {
            this.timeout = +initOptions.timeout;
        }
        if (initOptions.count_of_retries) {
            this.noOfRetries = +initOptions.count_of_retries;
        }
        if (initOptions.delay_between_retries) {
            this.delayTimeout = +initOptions.delay_between_retries;
        }
        this.http = axios.create({
            baseURL: initOptions.url,
            headers: {
                Accept: "application/json",
                "Content-type": "application/json",
                "Content-Encoding": "gzip, deflate, br",
                "Accpet-Charset": "utf-8",
            },
            timeout: this.timeout,
            withCredentials: false,
        });
        this.http.interceptors.request.use(async (config) => {
            const headers = config.headers;
            headers.setAuthorization(`Bearer ${this.resolveAccessToken()}`);
            headers.set("transaction-id", uuidv4());
            headers.set("X-API-KEY", apiKey);
            return config;
        }, (error) => Promise.reject(error));
        this.http.interceptors.response.use((response) => {
            return response;
        }, async function (error) {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
            const originalRequest = error.config;
            // token expired
            if ((((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && !originalRequest._retry) || (((_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.status) === 400 && ((_d = (_c = error === null || error === void 0 ? void 0 : error.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.code) === 5000)) {
                originalRequest._retry = true;
                const refreshToken = NADLService.prototype.availableStorage.getValue("REFRESH-TOKEN");
                const customerId = NADLService.prototype.availableStorage.getValue("customerId");
                const httpForAccessToken = axios.create({
                    baseURL: initOptions.url,
                    headers: {
                        Accept: "application/json",
                        "Content-type": "application/json",
                        "Content-Encoding": "gzip, deflate, br",
                        "Accpet-Charset": "utf-8",
                    },
                    timeout: 50000,
                    withCredentials: false,
                });
                httpForAccessToken.interceptors.request.use((config) => {
                    const headers = config.headers;
                    headers.setAuthorization(`Bearer ${refreshToken}`);
                    headers.set("transaction-id", uuidv4());
                    headers.set("X-API-KEY", initOptions.x_api_key);
                    return config;
                }, (error) => Promise.reject(error));
                try {
                    const response = await (httpForAccessToken === null || httpForAccessToken === void 0 ? void 0 : httpForAccessToken.post(`${initOptions.url}/individuals/${customerId}/access-token`, {}));
                    if ((response === null || response === void 0 ? void 0 : response.status) === 201 || (response === null || response === void 0 ? void 0 : response.status) === 200) {
                        const accessToken = response.headers["access-token"];
                        NADLService.prototype.availableStorage.setValue("ACCESS-TOKEN", accessToken);
                        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                        return axios(originalRequest);
                    }
                }
                catch (error) {
                    if (((_e = error === null || error === void 0 ? void 0 : error.response) === null || _e === void 0 ? void 0 : _e.status) !== 401) {
                        const code = (_g = (_f = error === null || error === void 0 ? void 0 : error.response) === null || _f === void 0 ? void 0 : _f.data) === null || _g === void 0 ? void 0 : _g.code;
                        if (code === 8000) {
                            return {
                                message: "Session Timed Out. Please restart the journey",
                                status: "SESSION_EXPIRED",
                                errorCode: ((_j = (_h = error === null || error === void 0 ? void 0 : error.response) === null || _h === void 0 ? void 0 : _h.data) === null || _j === void 0 ? void 0 : _j.code) || "",
                            };
                        }
                    }
                    else {
                        return {
                            message: "Some unknown error occurred",
                            status: "FAILURE",
                            errorCode: ((_l = (_k = error === null || error === void 0 ? void 0 : error.response) === null || _k === void 0 ? void 0 : _k.data) === null || _l === void 0 ? void 0 : _l.code) || "",
                        };
                    }
                }
            }
            return Promise.reject(error);
        });
        return new Promise((resolve) => resolve());
    }
    resolveAccessToken() {
        return this.availableStorage.getValue("ACCESS-TOKEN")
            ? this.availableStorage.getValue("ACCESS-TOKEN")
            : null;
    }
    async directMobilePreCheck(input) {
        var _a;
        const request = {
            vua: this.crypto.encrypt(input.mobileNum),
        };
        return (_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}/customers/fiu/login`, request).then((response) => {
            const data = response.data;
            if (data.code === 608) {
                this.availableStorage.setValue("userId", request.vua);
                this.availableStorage.setValue("isUserRegistered", true);
            }
            else {
                this.availableStorage.setValue("userId", request.vua);
                this.availableStorage.setValue("isUserRegistered", false);
            }
        }).catch((err) => {
            if (err.response) {
                if (err.response.data.code === 608) {
                    this.availableStorage.setValue("userId", request.vua);
                    this.availableStorage.setValue("isUserRegistered", true);
                }
                else {
                    this.availableStorage.setValue("userId", request.vua);
                    this.availableStorage.setValue("isUserRegistered", false);
                }
            }
        });
    }
    async authInit(input) {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            let aaOperation = undefined;
            try {
                let request;
                let promise;
                if (input.mobileNum) {
                    request = {
                        mobileNum: input.mobileNum,
                    };
                    aaOperation = "NADL_AUTH_MOBILE_CUSTOMERS-FIU-LOGIN";
                    promise = this.directMobilePreCheck(input)
                        .then(() => request);
                }
                else {
                    request = {
                        fi: input.encryptedFiuId,
                        ecreq: input.encryptedRequest,
                        reqdate: input.requestDate,
                    };
                    aaOperation = "NADL_AUTH_WEBVIEW_CUSTOMERS-FIU-VALIDATE";
                    promise = (_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}/customers/fiu/validate`, request).then((response) => {
                        const data = response.data;
                        this.availableStorage.setValue("userId", data.registered ? data.vua : data.mobile);
                        this.availableStorage.setValue("isUserRegistered", data.registered);
                        return {
                            mobileNum: data.registered ? data.vua : data.mobile,
                        };
                    });
                }
                return await (promise === null || promise === void 0 ? void 0 : promise.then(async (req) => await this.mobileAuthInit(req)).then((mappedResponse) => {
                    const { data, statusCode, id } = mappedResponse;
                    if (statusCode === 200 || (data === null || data === void 0 ? void 0 : data.statusCode) === 200) {
                        return {
                            statusCode: statusCode || (data === null || data === void 0 ? void 0 : data.statusCode),
                            status: "SEND",
                            otpReference: id || (data === null || data === void 0 ? void 0 : data.id),
                        };
                    }
                    else {
                        return mappedResponse;
                    }
                }));
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, aaOperation); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperNadl(attemptRequest()).getResponse("VALIDATE_USER");
    }
    async authVerify(input) {
        const login = "/customers/fiu/login?observe=response";
        const register = "/customers/fiu/register";
        let finalUrl = "";
        let type = "";
        let customerReq = {};
        const isUserRegistered = this.availableStorage.getValue("isUserRegistered");
        if (isUserRegistered === "true" || isUserRegistered === true) {
            customerReq = {
                mobile_validation_ID: input.otpReference,
                vua: this.availableStorage.getValue("userId"),
            };
            finalUrl = login;
            type = "AA_LOGIN";
        }
        else {
            customerReq = {
                consentReqId: "",
                //full_name : 'Customer',// make it 'Customer' for old one or send null - new or remove the key - working now
                mobile_no: this.availableStorage.getValue("userId"),
                mobile_validation_ID: this.availableStorage.getValue("mobileValidationId"),
                source: getDeviceType() || "WEB",
                vua: this.availableStorage.getValue("userId"),
            };
            finalUrl = register;
            type = "AA_REGISTER";
        }
        let retryCount = 0;
        if (input) {
            const attemptRequest = async () => {
                var _a, _b;
                const aaOperation = type === "AA_REGISTER"
                    ? "NADL_AUTH_CUSTOMERS-FIU-REGISTER"
                    : "NADL_AUTH_CUSTOMERS-FIU-LOGIN";
                try {
                    const request = {
                        otp: input.otp,
                        mobile_validation_id: input.otpReference,
                    };
                    const mobileAuthVerify = await this.mobileAuthVerify(request);
                    if ((_a = mobileAuthVerify === null || mobileAuthVerify === void 0 ? void 0 : mobileAuthVerify.data) === null || _a === void 0 ? void 0 : _a.is_validated) {
                        try {
                            const response = await ((_b = this.http) === null || _b === void 0 ? void 0 : _b.post(`${this.baseUrl}${finalUrl}`, customerReq));
                            if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                                //store customer id in session storage
                                const accessToken = response.headers["access-token"];
                                const refreshToken = response.headers["refresh-token"];
                                this.availableStorage.setValue("ACCESS-TOKEN", accessToken);
                                this.availableStorage.setValue("REFRESH-TOKEN", refreshToken);
                                this.availableStorage.setValue("customerId", response.data.id);
                                await this.getAccountCategories();
                                await this.getRejectionReasons();
                                await this.getFips();
                                return {
                                    status: "SUCCESS",
                                    statusCode: response === null || response === void 0 ? void 0 : response.status,
                                };
                            }
                        }
                        catch (error) {
                            if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                                await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                                retryCount++;
                                return attemptRequest(); // Retry the request
                            }
                            else {
                                return handleErrors(error, aaOperation); // Max retries reached
                            }
                        }
                    }
                    else if ((mobileAuthVerify === null || mobileAuthVerify === void 0 ? void 0 : mobileAuthVerify.status) === 400 || (mobileAuthVerify === null || mobileAuthVerify === void 0 ? void 0 : mobileAuthVerify.status) === "FAILURE" || (mobileAuthVerify === null || mobileAuthVerify === void 0 ? void 0 : mobileAuthVerify.status) === "AA_ERROR") {
                        console.warn(mobileAuthVerify.message);
                        return {
                            message: mobileAuthVerify === null || mobileAuthVerify === void 0 ? void 0 : mobileAuthVerify.message,
                            status: mobileAuthVerify === null || mobileAuthVerify === void 0 ? void 0 : mobileAuthVerify.status,
                            errorCode: (mobileAuthVerify === null || mobileAuthVerify === void 0 ? void 0 : mobileAuthVerify.errorCode) || "",
                            statusCode: mobileAuthVerify.statusCode,
                            aaOperation: mobileAuthVerify === null || mobileAuthVerify === void 0 ? void 0 : mobileAuthVerify.aaOperation,
                            attempt: mobileAuthVerify === null || mobileAuthVerify === void 0 ? void 0 : mobileAuthVerify.attempt,
                        };
                    }
                }
                catch (error) {
                    if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                        await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                        retryCount++;
                        return attemptRequest(); // Retry the request
                    }
                    else {
                        return handleErrors(error, aaOperation); // Max retries reached
                    }
                }
            };
            if (this.sendRawResponse) {
                return attemptRequest();
            }
            return new ResponseMapperNadl(attemptRequest()).getResponse("AA_LOGIN");
        }
    }
    async mobileAuthInit(input) {
        var _a;
        let encryptedMobileNum = input.mobileNum;
        if (((_a = input.mobileNum) === null || _a === void 0 ? void 0 : _a.length) === 10) {
            encryptedMobileNum = this.crypto.encrypt(input.mobileNum);
        }
        const req = {
            mobile_no: encryptedMobileNum,
        };
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            try {
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}/mobile-validations?observe=response`, req));
                if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                    this.availableStorage.setValue("mobileValidationId", response.data.id);
                    return {
                        status: "SEND",
                        id: response.data.id,
                        statusCode: response === null || response === void 0 ? void 0 : response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "NADL_AUTH_MOBILE-VALIDATIONS"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperNadl(attemptRequest()).getResponse("ALT_MOBILE");
    }
    async mobileAuthVerify(input) {
        const mobileValidationId = input.mobile_validation_id
            ? input.mobile_validation_id
            : this.availableStorage.getValue("mobileValidationId");
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            try {
                const confirmRequest = {
                    challenge_response: input.otp,
                };
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}/mobile-validations/${mobileValidationId}/challenge-response?observe=response`, confirmRequest));
                if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                    return {
                        status: "ACCEPT",
                        message: "Mobile Authentication Successful",
                        data: response.data,
                        statusCode: response === null || response === void 0 ? void 0 : response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "NADL_AUTH_MOBILE-VALIDATIONS-RESPONSE"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperNadl(attemptRequest()).getResponse("CONFIRM_ALT_MOBILE");
    }
    async getLinkedAccounts() {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            try {
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.get(`${this.baseUrl}/linked-accounts`));
                if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                    return {
                        status: "SUCCESS",
                        LinkedAccounts: getMappedLinkedAccounts(response.data.accounts),
                        statusCode: response === null || response === void 0 ? void 0 : response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "NADL_LINKED-ACCOUNTS"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperNadl(attemptRequest()).getResponse("LINKED_ACCOUNTS");
    }
    async discoverAccounts(input) {
        var _a, _b, _c, _d;
        let retryCount = 0;
        //await this.getAccountCategories();
        /*  const isLoggedInUser = checkForAltMobileNumber(
         input?.Customer?.Identifiers[0].value || "",
         this.crypto,
         this.availableStorage
         ); */
        const isLoggedInUser = checkForAltMobileNumber(((_a = input === null || input === void 0 ? void 0 : input.Customer) === null || _a === void 0 ? void 0 : _a.Identifiers.filter((el) => el.type === "MOBILE")[0].value) || "", this.crypto, this.availableStorage);
        //try {
        const ids = [];
        (_b = input.Customer) === null || _b === void 0 ? void 0 : _b.Identifiers.forEach((data) => {
            ids.push({
                type: data.type,
                value: this.crypto.encrypt(data.value),
            });
        });
        if (!this.listOfFips || this.listOfFips.length === 0) {
            await this.getFips(true);
        }
        const fiTypesAvailable = getFITypes((_c = input === null || input === void 0 ? void 0 : input.FIPDetails) === null || _c === void 0 ? void 0 : _c.fipId, this.listOfFips, input === null || input === void 0 ? void 0 : input.FITypes);
        if (fiTypesAvailable && fiTypesAvailable.length > 0) {
            const request = {
                //resend_otp: false,
                //mobile_validation_ID: this.availableStorage.getValue('mobileValidationId'),
                //challenge_response: "",
                //access_token: this.availableStorage.getValue('ACCESS-TOKEN'),
                //account_types: getAccTypes(this.accountCategories),
                account_types: fiTypesAvailable,
                fip_id: (_d = input === null || input === void 0 ? void 0 : input.FIPDetails) === null || _d === void 0 ? void 0 : _d.fipId,
                identifiers: ids,
                /* identifiers: [
                 {
                 type: input?.Customer?.Identifiers[0].type,
                 value: this.crypto.encrypt(input?.Customer?.Identifiers[0].value),
                 },
                 ], */
                ...(!isLoggedInUser &&
                    this.availableStorage.getValue("mobileValidationId") !== null && {
                    mobile_validation_ID: this.availableStorage.getValue("mobileValidationId"),
                }),
            };
            const attemptRequest = async () => {
                var _a, _b;
                try {
                    const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}/accounts/discover`, request));
                    if ((response === null || response === void 0 ? void 0 : response.status) === 201 || (response === null || response === void 0 ? void 0 : response.status) === 200) {
                        return {
                            status: "SUCCESS",
                            DiscoveredAccounts: getMappedDiscoveredAccounts((_b = response === null || response === void 0 ? void 0 : response.data) === null || _b === void 0 ? void 0 : _b.discovered_accounts),
                            statusCode: response === null || response === void 0 ? void 0 : response.status,
                        };
                    }
                }
                catch (error) {
                    if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                        await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                        retryCount++;
                        return attemptRequest(); // Retry the request
                    }
                    else {
                        return handleErrors(error, "NADL_ACCOUNTS-DISCOVER"); // Max retries reached
                    }
                }
            };
            if (this.sendRawResponse) {
                return attemptRequest();
            }
            return new ResponseMapperNadl(attemptRequest()).getResponse("ACCOUNT_DISCOVERY");
        }
        else {
            return Promise.reject(new ResponseError("ERROR", {
                status: "ERROR",
                errorCode: "FIP_UNAVAILABLE",
                errorMessage: "Financial Institution is not available",
                statusCode: 404,
            }, "Financial Institution is not available"));
        }
    }
    async linkAccounts(input) {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a, _b, _c;
            try {
                const request = {
                    fip_id: (_a = input === null || input === void 0 ? void 0 : input.FIPDetails) === null || _a === void 0 ? void 0 : _a.fipId,
                    accounts: (_b = input === null || input === void 0 ? void 0 : input.Customer) === null || _b === void 0 ? void 0 : _b.Accounts.map((data) => data.id),
                };
                this.availableStorage.setValue("linkingFip", request.fip_id || "");
                const response = await ((_c = this.http) === null || _c === void 0 ? void 0 : _c.post(`${this.baseUrl}/accounts/link`, request));
                if ((response === null || response === void 0 ? void 0 : response.status) === 201 || (response === null || response === void 0 ? void 0 : response.status) === 200) {
                    return {
                        status: "SUCCESS",
                        AuthenticatorType: response.data.authenticator_type,
                        RefNumber: response.data.account_link_req_ref_number,
                        statusCode: response === null || response === void 0 ? void 0 : response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "NADL_ACCOUNTS-LINK"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperNadl(attemptRequest()).getResponse("ACCOUNT_LINKING");
    }
    async confirmLinking(input) {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            try {
                const request = {
                    fip_id: this.availableStorage.getValue("linkingFip"),
                    account_link_req_ref_number: input.AccountsLinkingRefNumber,
                    token: input.token,
                };
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}/accounts/confirm-link`, request));
                if ((response === null || response === void 0 ? void 0 : response.status) === 201) {
                    this.availableStorage.removeValue("linkingFip");
                    return {
                        status: "SUCCESS",
                        ...getMappedLinkingRes(response.data),
                        statusCode: response === null || response === void 0 ? void 0 : response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "NADL_ACCOUNTS-CONFIRM-LINK"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperNadl(attemptRequest()).getResponse("CONFIRM_ACCOUNT_LINKING");
    }
    async consentRequestDetails(consentHandle) {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            try {
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.get(`${this.baseUrl}/consent-requests/${consentHandle}`));
                if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                    const data = response.data;
                    return {
                        ...getConsentRequestDetailsMapped(data),
                        statusCode: response === null || response === void 0 ? void 0 : response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "NADL_GET_CONSENT-REQUESTS"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperNadl(attemptRequest()).getResponse("CONSENT_DETAILS");
    }
    async consent(input) {
        let retryCount = 0;
        let account_IDs = [];
        const type = input.handleStatus === ConsentAction.ACCEPT
            ? "CONSENT_ACTION_APPROVE"
            : "CONSENT_ACTION_REJECT";
        const attemptRequest = async () => {
            var _a, _b;
            try {
                if (input.handleStatus === ConsentAction.ACCEPT) {
                    account_IDs = input.FIPDetails.flatMap(obj => obj.Accounts.map(item => item.id));
                    const request = {
                        account_IDs: account_IDs,
                        resend_otp: false,
                    };
                    const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}/consent-requests/${input.consentHandleId}/approve`, request));
                    if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                        return {
                            status: "SUCCESS",
                            message: "Consent Stored",
                            statusCode: response === null || response === void 0 ? void 0 : response.status,
                        };
                    }
                }
                else {
                    //await this.getRejectionReasons();
                    const rejectReq = {
                        id: this.rejectionReasons[0].id,
                        resend_otp: false,
                    };
                    const response = await ((_b = this.http) === null || _b === void 0 ? void 0 : _b.post(`${this.baseUrl}/consent-requests/${input.consentHandleId}/reject`, rejectReq));
                    if ((response === null || response === void 0 ? void 0 : response.status) === 200 || (response === null || response === void 0 ? void 0 : response.status) === 201) {
                        return {
                            status: "SUCCESS",
                            message: "Consent Stored",
                            statusCode: response === null || response === void 0 ? void 0 : response.status,
                        };
                    }
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "NADL_" + type); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperNadl(attemptRequest()).getResponse("CONSENT_ACTION");
    }
    async logout() {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            try {
                const customerId = this.availableStorage.getValue("customerId");
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.post(`${this.baseUrl}/individuals/${customerId}/logout`, {}));
                if ((response === null || response === void 0 ? void 0 : response.status) === 204) {
                    this.cleanUp();
                    return;
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries || error.code === AxiosError.ERR_NETWORK) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "NADL_INDIVIDUAL_LOGOUT"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperNadl(attemptRequest()).getResponse("LOGOUT");
    }
    async getUserInfo() {
        let retryCount = 0;
        const attemptRequest = async () => {
            var _a;
            try {
                const customerId = this.availableStorage.getValue("customerId");
                const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.get(`${this.baseUrl}/individuals/${customerId}`));
                if ((response === null || response === void 0 ? void 0 : response.status) === 200) {
                    return {
                        status: "ACCEPT",
                        UserInfo: {
                            mobileNo: this.crypto.decrypt(response.data.mobile_no),
                            userId: this.crypto.decrypt(response.data.vua),
                        },
                        statusCode: response === null || response === void 0 ? void 0 : response.status,
                    };
                }
            }
            catch (error) {
                if (error instanceof AxiosError && !error.response && (error.code === AxiosError.ERR_CANCELED || error.code === AxiosError.ECONNABORTED || error.code === AxiosError.ERR_NETWORK) && retryCount < this.noOfRetries || error.code === AxiosError.ERR_NETWORK) {
                    await new Promise(resolve => setTimeout(resolve, this.delayTimeout)); // delaying the request if error occures
                    retryCount++;
                    return attemptRequest(); // Retry the request
                }
                else {
                    return handleErrors(error, "NADL_INDIVIDUAL_PROFILE"); // Max retries reached
                }
            }
        };
        if (this.sendRawResponse) {
            return attemptRequest();
        }
        return new ResponseMapperNadl(attemptRequest()).getResponse("USER_INFO");
    }
    async getAccountCategories() {
        var _a;
        try {
            const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.get(`${this.baseUrl}/accounts/categories`));
            if ((response === null || response === void 0 ? void 0 : response.status) === 201) {
                this.accountCategories = response.data.account_categories;
            }
        }
        catch (error) {
            return handleErrors(error, "NADL_UTILS_ACCOUNTS-CATEGORIES");
        }
    }
    async getRejectionReasons() {
        var _a;
        try {
            const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.get(`${this.baseUrl}/reject-reasons`));
            if ((response === null || response === void 0 ? void 0 : response.status) === 201 || (response === null || response === void 0 ? void 0 : response.status) === 200) {
                this.rejectionReasons = response.data.reject_reasons;
            }
        }
        catch (error) {
            return handleErrors(error, "NADL_UTILS_REJECT-REASONS");
        }
    }
    cleanUp() {
        if (this.availableStorage) {
            this.availableStorage.removeAllValues();
        }
    }
    async getFips(retry) {
        var _a;
        try {
            const response = await ((_a = this.http) === null || _a === void 0 ? void 0 : _a.get(`${this.baseUrl}/FIPs`, { timeout: retry ? 2 * this.timeout : this.timeout }));
            if ((response === null || response === void 0 ? void 0 : response.status) === 201 || (response === null || response === void 0 ? void 0 : response.status) === 200) {
                this.listOfFips = response.data.FIPs;
            }
        }
        catch (error) {
            return handleErrors(error, "NADL_UTILS_GET-FIPS");
        }
    }
}
//# sourceMappingURL=aasdk-nadl.js.map